import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ShieldAlert, MessageSquareOff, CheckCircle2, FileText, Cookie, UserCheck } from 'lucide-react';
import client from '../../api/client';
import LegalConfigEditor from './LegalConfigEditor';
import DocPublisher from './DocPublisher';
import PrivacyQueue from './PrivacyQueue';
import SuppressionManager from './SuppressionManager';
import ConsentLog from './ConsentLog';

const Stat = ({ icon: Icon, label, value, sub, testId }) => (
  <div data-testid={testId} className="bg-white rounded-2xl border border-[#EFE3D8] p-4">
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#9A8A7E]"><Icon className="w-3.5 h-3.5" /> {label}</div>
    <p className="font-serif text-3xl text-[#2A2A2A] mt-1">{value}</p>
    {sub && <p className="text-[11px] text-[#9A8A7E] mt-0.5">{sub}</p>}
  </div>
);

export default function CompliancePanel({ toast }) {
  const [ov, setOv] = useState(null);
  const load = useCallback(() => client.get('/admin/compliance/overview').then(({ data }) => setOv(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!ov) return <div className="py-10 flex justify-center text-[#6A6A6A]"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const pr = ov.privacy_request_counts || {};
  const openPr = (pr.received || 0) + (pr.verifying || 0) + (pr.in_progress || 0);

  return (
    <div data-testid="compliance-panel" className="space-y-6">
      {ov.missing_placeholders.length > 0 && (
        <div data-testid="compliance-missing-placeholders" className="rounded-2xl p-4 bg-[#FBF1EA] border border-[#EFC9B0] flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-[#A15A2A] flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-[#7A3E1A]">Blank fields showing on public legal pages</p>
            <p className="text-xs text-[#8A5A3A]">{ov.missing_placeholders.join(', ')} — fill these in below before going live.</p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Stat testId="stat-opt-ins" icon={CheckCircle2} label="SMS opt-ins (30d)" value={ov.opt_ins_30d} sub={`${ov.consent_counts.opt_in || 0} all-time`} />
        <Stat testId="stat-stops" icon={MessageSquareOff} label="STOPs (30d)" value={ov.stops_30d} sub={`${ov.suppressed_numbers} numbers blocked`} />
        <Stat testId="stat-blocked" icon={ShieldAlert} label="Blocked sends (30d)" value={ov.blocked_sends_30d} />
        <Stat testId="stat-host-auth" icon={UserCheck} label="Hosts authorized" value={ov.host_authorizations} sub={`of ${ov.total_hosts} accounts`} />
        <Stat testId="stat-privacy" icon={FileText} label="Open privacy requests" value={openPr} sub={`${pr.completed || 0} completed`} />
        <Stat testId="stat-cookies" icon={Cookie} label="Cookie notices (30d)" value={ov.cookie_notices_30d} />
      </div>
      <PrivacyQueue toast={toast} onChange={load} />
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <LegalConfigEditor config={ov.config} toast={toast} onSaved={load} />
        <DocPublisher config={ov.config} toast={toast} onPublished={load} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <SuppressionManager toast={toast} onChange={load} />
        <ConsentLog />
      </div>
    </div>
  );
}
